import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, App } from 'antd';
import { LockOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import userService from '../services/userService';

const { Title, Text } = Typography;

const ResetPassword = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      message.error('Invalid password reset link');
      navigate('/login');
    }
  }, [token, navigate]);

  const onFinish = async (values) => {
    if (!token) {
      message.error('Invalid password reset token');
      return;
    }

    setLoading(true);
    try {
      const result = await userService.resetPassword(token, values.password);
      
      if (result.success) {
        message.success('Password reset successfully! You can now login with your new password.');
        navigate('/login');
      } else {
        message.error(result.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      message.error('Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return null; // Will redirect in useEffect
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px'
    }}>
      <Card style={{ 
        width: '100%', 
        maxWidth: 400, 
        padding: '20px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        borderRadius: '8px'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <Title level={2} style={{ color: '#2E86AB', marginBottom: '8px', fontWeight: 'bold' }}>
           <div className="mb-4">
          <img 
            src="/logo.png" 
            alt="TapLearnGo Logo"
            className="w-3/4 h-auto mx-auto"
            style={{ backgroundColor: 'transparent', marginTop: '-20px' }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
          </Title>
          <Text type="secondary">Creating a culture of learning</Text>
        </div>

        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
          <Title level={4} style={{ margin: 0 }}>Set New Password</Title>
          <Text type="secondary" style={{ fontSize: '14px', marginTop: '8px', display: 'block' }}>
            Enter your new password below.
          </Text>
        </div>

        <Form
          form={form}
          name="reset-password"
          onFinish={onFinish}
          layout="vertical"
          size="large"
        >
          <Form.Item
            name="password"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter your new password!' },
              { min: 6, message: 'Password must be at least 6 characters!' }
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="Enter new password" 
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm New Password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm your new password!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="Confirm new password" 
            />
          </Form.Item>

          <Form.Item style={{ marginTop: '24px', marginBottom: '16px' }}>
            <Button 
              type="primary"
              htmlType="submit" 
              loading={loading}
              style={{ 
                width: '100%', 
                height: '40px',
                background: '#2E86AB',
                borderColor: '#2E86AB',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              Reset Password
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center' }}>
            <Button 
              type="link" 
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/login')}
            >
              Back to Login
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default ResetPassword;